import type { AllocationItem, CreateEntryRequest } from "@/api/types";

/**
 * One entry queued while offline, waiting to be replayed against the real
 * API (of-002 -> of-003, `docs/demo/tradeshow-2026-09.md` "Operator journey").
 */
export interface QueuedEntry {
  /** Generated on-device — never sent to the server. Only used to find/remove this row. */
  clientId: string;
  request: CreateEntryRequest;
  /**
   * Set only when this entry came from a receipt scan the visitor accepted
   * or corrected — the same condition `QuickEntry.tsx` already uses to
   * decide whether to call `confirmMapping` online. Carried here so
   * `outbox.ts`'s `drain` can fire that same write after a successful
   * offline-queued submission — the "it learned" beat must survive going
   * offline AFTER a scan but BEFORE the visitor taps submit, not just a scan
   * that happens while already offline (see `scanQueue.ts` for that case).
   * `null`/absent for a plain manual entry, which never taught a mapping.
   */
  mapping?: { vendorId: string; accountId: string } | null;
  /** epoch ms — the ordering key. Replay must be strictly oldest-first (of-003). */
  queuedAt: number;
}

export interface OutboxStore {
  add(item: QueuedEntry): Promise<void>;
  remove(clientId: string): Promise<void>;
  all(): Promise<QueuedEntry[]>;
}

const DB_NAME = "easyfarm-offline";
/**
 * ef-017: bumped 1 -> 2 to add `PUNCH_STORE_NAME` alongside the existing entry outbox,
 * in the SAME database rather than a second `indexedDB.open` — two opens of one
 * database at different versions is a version-conflict `onblocked` waiting to happen,
 * and there is nothing about a punch queue that needs its own database. `onupgradeneeded`
 * fires once for a v1 -> v2 open and leaves the existing `outbox` store (and everything
 * already queued in it) untouched; it only ever ADDS the store that is missing.
 *
 * ef-040-5: bumped 2 -> 3 for the identical reason, adding `TIME_OFF_STORE_NAME` — a
 * third queue, same database, same "only ever ADD" upgrade path.
 */
const DB_VERSION = 3;
const STORE_NAME = "outbox";
const PUNCH_STORE_NAME = "punches";
const TIME_OFF_STORE_NAME = "timeOff";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "clientId" });
      }
      if (!db.objectStoreNames.contains(PUNCH_STORE_NAME)) {
        db.createObjectStore(PUNCH_STORE_NAME, { keyPath: "clientPunchId" });
      }
      if (!db.objectStoreNames.contains(TIME_OFF_STORE_NAME)) {
        db.createObjectStore(TIME_OFF_STORE_NAME, { keyPath: "clientRequestId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
    req.onblocked = () => reject(new Error("IndexedDB open blocked"));
  });
}

/**
 * IndexedDB-backed store — the primary implementation. This is what survives
 * a reload: a demo phone left in airplane mode, bumped, and reloaded must not
 * lose the queued entry.
 */
class IndexedDbOutboxStore implements OutboxStore {
  constructor(private readonly db: IDBDatabase) {}

  add(item: QueuedEntry): Promise<void> {
    return this.run("readwrite", (store) => store.put(item));
  }

  remove(clientId: string): Promise<void> {
    return this.run("readwrite", (store) => store.delete(clientId));
  }

  all(): Promise<QueuedEntry[]> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORE_NAME, "readonly");
      const req = tx.objectStore(STORE_NAME).getAll();
      req.onsuccess = () =>
        resolve((req.result as QueuedEntry[]).slice().sort((a, b) => a.queuedAt - b.queuedAt));
      req.onerror = () => reject(req.error ?? new Error("IndexedDB read failed"));
    });
  }

  private run(mode: IDBTransactionMode, op: (store: IDBObjectStore) => IDBRequest): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORE_NAME, mode);
      const req = op(tx.objectStore(STORE_NAME));
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error ?? new Error("IndexedDB write failed"));
    });
  }
}

/**
 * In-memory fallback for when IndexedDB does not actually work — Safari
 * private browsing being the case named in the task. Keeps the outbox
 * WORKING for the current tab; it just does not survive a reload, which is
 * the one thing IndexedDB buys us and private mode takes away. A storage
 * failure must never surface as an error to the visitor, so this is the
 * degrade path rather than a thrown exception.
 */
class MemoryOutboxStore implements OutboxStore {
  private items = new Map<string, QueuedEntry>();

  async add(item: QueuedEntry): Promise<void> {
    this.items.set(item.clientId, item);
  }

  async remove(clientId: string): Promise<void> {
    this.items.delete(clientId);
  }

  async all(): Promise<QueuedEntry[]> {
    return [...this.items.values()].sort((a, b) => a.queuedAt - b.queuedAt);
  }
}

/**
 * Picks IndexedDB when it actually works, memory when it doesn't — decided
 * by attempting a real write, not by feature-detecting `"indexedDB" in
 * window`. Safari private browsing has `indexedDB` present but can throw (or
 * silently misbehave) the moment you touch it, so presence alone would pick
 * the store that then fails on the first real entry. This is the same
 * "verify the verification" instinct as a negative control (`rules/core.md`
 * #5): a probe that cannot fail proves nothing, so this one does a real
 * put + delete against a throwaway key before trusting the result.
 */
export async function createOutboxStore(): Promise<OutboxStore> {
  try {
    if (typeof indexedDB === "undefined") throw new Error("indexedDB unavailable");
    const db = await openDb();
    const store = new IndexedDbOutboxStore(db);
    const probeId = `__probe__${Date.now()}`;
    await store.add({
      clientId: probeId,
      request: { entityId: "", vendorId: "", accountId: "", amount: 0 },
      queuedAt: 0,
    });
    await store.remove(probeId);
    return store;
  } catch (err) {
    console.warn(
      "IndexedDB is unavailable (likely private browsing) — offline entries will be " +
        "held in memory for this tab only and will not survive a reload.",
      err,
    );
    return new MemoryOutboxStore();
  }
}

/**
 * ef-017 — one punch (clock-in or clock-out) queued while offline, waiting to replay
 * against ef-016's idempotent `/punch-in` / `/punch-out`. Deliberately its own store
 * rather than shoehorned into `QueuedEntry` above: a punch carries no `CreateEntryRequest`
 * and needs a `kind`, and forcing the two shapes to share a store would mean every reader
 * of one queue has to reason about fields that only make sense for the other.
 */
export interface QueuedPunch {
  /** Generated on-device — this becomes the wire `clientPunchId` on replay (never on a
   *  live, first attempt — see `TimeClock.tsx`'s `punch()`). Also this store's key, so a
   *  replay attempt can remove exactly the row it just resolved. */
  clientPunchId: string;
  kind: "in" | "out";
  /** The phone's own clock AT THE TAP — captured by the caller before this is ever
   *  written, and never re-derived here. ISO 8601 with a `Z` offset. */
  recordedAt: string;
  latitude?: number;
  longitude?: number;
  /** Punch-out only; ignored server-side on a replayed clock-in exactly as it is on a
   *  live one (`TimeContracts.cs` `PunchRequest.WorkerNote`). */
  workerNote?: string;
  breakMinutes?: number;
  /**
   * ef-024 — set when the worker chose "what this was for" WHILE this punch-out was
   * still sitting in the queue (no server row to PUT against yet). Attached AFTER
   * `enqueue` via `punchOutbox.attachAllocations`, never at enqueue time — the choice
   * happens on the panel that opens once this item exists, not before. Absent/`[]` means
   * skipped, same convention `Allocation[]` on `TimeEntry` already uses. Read by
   * `drainOnce` once the paired punch-out has actually landed and returned a real id.
   */
  allocations?: AllocationItem[];
  /** epoch ms — the ordering key. Replay must be strictly oldest-first, exactly like
   *  the entry outbox's own `queuedAt`. */
  queuedAt: number;
}

export interface PunchOutboxStore {
  add(item: QueuedPunch): Promise<void>;
  remove(clientPunchId: string): Promise<void>;
  all(): Promise<QueuedPunch[]>;
}

/** Mirrors `IndexedDbOutboxStore` above field-for-field — see that class's own remarks
 *  for why IndexedDB is the primary store (a phone left in airplane mode and reloaded
 *  must not lose a queued punch either). */
class IndexedDbPunchOutboxStore implements PunchOutboxStore {
  constructor(private readonly db: IDBDatabase) {}

  add(item: QueuedPunch): Promise<void> {
    return this.run("readwrite", (store) => store.put(item));
  }

  remove(clientPunchId: string): Promise<void> {
    return this.run("readwrite", (store) => store.delete(clientPunchId));
  }

  all(): Promise<QueuedPunch[]> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(PUNCH_STORE_NAME, "readonly");
      const req = tx.objectStore(PUNCH_STORE_NAME).getAll();
      req.onsuccess = () =>
        resolve((req.result as QueuedPunch[]).slice().sort((a, b) => a.queuedAt - b.queuedAt));
      req.onerror = () => reject(req.error ?? new Error("IndexedDB read failed"));
    });
  }

  private run(mode: IDBTransactionMode, op: (store: IDBObjectStore) => IDBRequest): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(PUNCH_STORE_NAME, mode);
      const req = op(tx.objectStore(PUNCH_STORE_NAME));
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error ?? new Error("IndexedDB write failed"));
    });
  }
}

/** Mirrors `MemoryOutboxStore` above — see its remarks on why a storage failure
 *  degrades rather than throws. */
class MemoryPunchOutboxStore implements PunchOutboxStore {
  private items = new Map<string, QueuedPunch>();

  async add(item: QueuedPunch): Promise<void> {
    this.items.set(item.clientPunchId, item);
  }

  async remove(clientPunchId: string): Promise<void> {
    this.items.delete(clientPunchId);
  }

  async all(): Promise<QueuedPunch[]> {
    return [...this.items.values()].sort((a, b) => a.queuedAt - b.queuedAt);
  }
}

/** Mirrors `createOutboxStore` above — same "verify by a real write" reasoning, same
 *  degrade-to-memory path for Safari private browsing. */
export async function createPunchOutboxStore(): Promise<PunchOutboxStore> {
  try {
    if (typeof indexedDB === "undefined") throw new Error("indexedDB unavailable");
    const db = await openDb();
    const store = new IndexedDbPunchOutboxStore(db);
    const probeId = `__probe__${Date.now()}`;
    await store.add({ clientPunchId: probeId, kind: "in", recordedAt: new Date(0).toISOString(), queuedAt: 0 });
    await store.remove(probeId);
    return store;
  } catch (err) {
    console.warn(
      "IndexedDB is unavailable (likely private browsing) — offline punches will be " +
        "held in memory for this tab only and will not survive a reload.",
      err,
    );
    return new MemoryPunchOutboxStore();
  }
}

/**
 * ef-040-5 — one time-off request queued while offline, waiting to replay against
 * `POST /api/demo/time-off`'s own idempotent `clientRequestId`
 * (db/migrations/0039_time_off_offline.sql). Deliberately its own store rather than
 * shoehorned into `QueuedPunch` above — a time-off submission carries a date range and
 * hours, not GPS/break minutes, and the two queues drain against completely different
 * routes.
 */
export interface QueuedTimeOff {
  /** Generated on-device — this becomes the wire `clientRequestId` on replay (never on a
   *  live, first attempt — see `TimeOffRequests.tsx`'s submit handler). Also this store's
   *  key, so a replay attempt can remove exactly the row it just resolved. */
  clientRequestId: string;
  startDate: string;
  endDate: string;
  hoursRequested: number;
  reason?: string;
  /** The phone's own clock AT THE TAP — captured by the caller before this is ever
   *  written, and never re-derived here. ISO 8601 with a `Z` offset. Becomes the row's own
   *  `createdAt` server-side (ScheduleRepository.SubmitTimeOffAsync's own remarks) — the
   *  "filed when" fact, not merely a replay bound. */
  recordedAt: string;
  /** epoch ms — the ordering key. Replay must be strictly oldest-first, same as the other
   *  two queues' own `queuedAt`. */
  queuedAt: number;
}

export interface TimeOffOutboxStore {
  add(item: QueuedTimeOff): Promise<void>;
  remove(clientRequestId: string): Promise<void>;
  all(): Promise<QueuedTimeOff[]>;
}

/** Mirrors `IndexedDbPunchOutboxStore` above field-for-field. */
class IndexedDbTimeOffOutboxStore implements TimeOffOutboxStore {
  constructor(private readonly db: IDBDatabase) {}

  add(item: QueuedTimeOff): Promise<void> {
    return this.run("readwrite", (store) => store.put(item));
  }

  remove(clientRequestId: string): Promise<void> {
    return this.run("readwrite", (store) => store.delete(clientRequestId));
  }

  all(): Promise<QueuedTimeOff[]> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(TIME_OFF_STORE_NAME, "readonly");
      const req = tx.objectStore(TIME_OFF_STORE_NAME).getAll();
      req.onsuccess = () =>
        resolve((req.result as QueuedTimeOff[]).slice().sort((a, b) => a.queuedAt - b.queuedAt));
      req.onerror = () => reject(req.error ?? new Error("IndexedDB read failed"));
    });
  }

  private run(mode: IDBTransactionMode, op: (store: IDBObjectStore) => IDBRequest): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(TIME_OFF_STORE_NAME, mode);
      const req = op(tx.objectStore(TIME_OFF_STORE_NAME));
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error ?? new Error("IndexedDB write failed"));
    });
  }
}

/** Mirrors `MemoryPunchOutboxStore` above — see its remarks on why a storage failure
 *  degrades rather than throws. */
class MemoryTimeOffOutboxStore implements TimeOffOutboxStore {
  private items = new Map<string, QueuedTimeOff>();

  async add(item: QueuedTimeOff): Promise<void> {
    this.items.set(item.clientRequestId, item);
  }

  async remove(clientRequestId: string): Promise<void> {
    this.items.delete(clientRequestId);
  }

  async all(): Promise<QueuedTimeOff[]> {
    return [...this.items.values()].sort((a, b) => a.queuedAt - b.queuedAt);
  }
}

/** Mirrors `createPunchOutboxStore` above — same "verify by a real write" reasoning, same
 *  degrade-to-memory path for Safari private browsing. */
export async function createTimeOffOutboxStore(): Promise<TimeOffOutboxStore> {
  try {
    if (typeof indexedDB === "undefined") throw new Error("indexedDB unavailable");
    const db = await openDb();
    const store = new IndexedDbTimeOffOutboxStore(db);
    const probeId = `__probe__${Date.now()}`;
    await store.add({
      clientRequestId: probeId,
      startDate: "1970-01-01",
      endDate: "1970-01-01",
      hoursRequested: 0,
      recordedAt: new Date(0).toISOString(),
      queuedAt: 0,
    });
    await store.remove(probeId);
    return store;
  } catch (err) {
    console.warn(
      "IndexedDB is unavailable (likely private browsing) — offline time-off requests " +
        "will be held in memory for this tab only and will not survive a reload.",
      err,
    );
    return new MemoryTimeOffOutboxStore();
  }
}
